import React, { ReactNode } from 'react';

/**
 * 🍎 Badge Component - Apple-style modern badge/pill
 * 
 * Variants:
 * - primary: Blue badge
 * - secondary: Gray badge
 * - success: Green badge
 * - warning: Yellow badge
 * - danger: Red badge
 * - info: Teal badge
 */

interface BadgeProps {
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';
  size?: 'small' | 'medium' | 'large';
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
  dot?: boolean;
}

const Badge: React.FC<BadgeProps> = ({
  variant = 'primary',
  size = 'medium',
  children,
  icon,
  className = '',
  dot = false,
}) => {
  // Variant classes
  const variantClasses = {
    primary: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    secondary: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
    success: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    danger: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    info: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
  };

  // Size classes
  const sizeClasses = {
    small: 'px-2 py-0.5 text-xs gap-1',
    medium: 'px-3 py-1 text-sm gap-1.5',
    large: 'px-4 py-1.5 text-base gap-2',
  };

  // Dot color classes
  const dotColorClasses = {
    primary: 'bg-primary-600 dark:bg-primary-400',
    secondary: 'bg-gray-600 dark:bg-gray-400',
    success: 'bg-green-600 dark:bg-green-400',
    warning: 'bg-yellow-600 dark:bg-yellow-400',
    danger: 'bg-red-600 dark:bg-red-400',
    info: 'bg-teal-600 dark:bg-teal-400',
  };

  const dotSizeClasses = {
    small: 'w-1.5 h-1.5',
    medium: 'w-2 h-2',
    large: 'w-2.5 h-2.5',
  };

  return (
    <span
      className={`
        inline-flex items-center rounded-full font-medium transition-colors
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${className}
      `}
    >
      {dot && (
        <span
          className={`
            rounded-full
            ${dotColorClasses[variant]}
            ${dotSizeClasses[size]}
          `}
        />
      )}
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{children}</span>
    </span>
  );
};

export default Badge;

